import React from "react";
import { Star } from "lucide-react";
import { tr } from "../lib/i18n";

/**
 * Single row of 5 compact brand-badge tiles.
 * Each tile: brand-colored square with OFFICIAL LOGO IMAGE + rating + count.
 */

// Multicolor Google "G" (official brand geometry) — inline SVG stays because the reference is clean
const GoogleGIcon = ({ className }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const BRANDS = {
  google: {
    bg: "#ffffff",
    border: "#e5e7eb",
    accent: "#4285F4",
    render: () => <GoogleGIcon className="h-11 w-11 sm:h-12 sm:w-12" />,
  },
  tripadvisor_restaurant: {
    bg: "#ffffff",
    border: "#e5e7eb",
    accent: "#00AF87",
    render: () => (
      <img
        src="/logos/tripadvisor_restaurant.webp"
        alt="TripAdvisor Ristorante"
        className="h-full w-full object-contain"
        style={{ transform: "scale(1.35)" }}
      />
    ),
  },
  tripadvisor_hotel: {
    // Classic owl variant — logo already has red/green/yellow colors, keep white bg
    bg: "#ffffff",
    border: "#e5e7eb",
    accent: "#00A758",
    render: () => (
      <img
        src="/logos/tripadvisor_hotel.png"
        alt="TripAdvisor Hotel"
        className="h-full w-full object-contain"
        style={{ transform: "scale(1.25)" }}
      />
    ),
  },
  thefork: {
    bg: "#ffffff",
    border: "#e5e7eb",
    accent: "#046C4E",
    render: () => (
      <img
        src="/logos/thefork.webp"
        alt="TheFork"
        className="h-full w-full object-contain"
        style={{ transform: "scale(1.3)" }}
      />
    ),
  },
  booking: {
    // Inner tile fully BLUE with white "Booking." wordmark filling it.
    bg: "#003580",
    border: "#003580",
    accent: "#003580",
    render: () => (
      <div className="flex h-full w-full items-center justify-center">
        <span className="font-black italic tracking-tight text-white text-[13px] sm:text-[15px] leading-none">
          Booking<span className="text-[#FEBB02]">.</span>
        </span>
      </div>
    ),
  },
};

const SUB_LABEL = {
  tripadvisor_restaurant: { it: "Ristorante", en: "Restaurant", es: "Restaurante", fr: "Restaurant", de: "Restaurant" },
  tripadvisor_hotel: { it: "Hotel", en: "Hotel", es: "Hotel", fr: "Hôtel", de: "Hotel" },
};

function PlatformTile({ platform, lang }) {
  const brand = BRANDS[platform.id] || {
    bg: "#4a1c17", border: "#4a1c17", accent: "#c48b31", render: () => null,
  };
  const scale = platform.scale || 5;
  const rating = Number(platform.rating || 0);
  const count = Number(platform.count || 0);
  const sub = SUB_LABEL[platform.id];

  return (
    <a
      href={platform.url || "#"}
      target="_blank"
      rel="noreferrer"
      data-testid={`review-badge-${platform.id}`}
      className="group flex flex-col items-center gap-2 rounded-2xl border border-[#e8dcc8] bg-white p-2.5 sm:p-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
    >
      {/* Big brand tile */}
      <div
        className="relative flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-xl shrink-0 overflow-hidden"
        style={{ backgroundColor: brand.bg, borderWidth: 1, borderColor: brand.border }}
      >
        {brand.render()}
      </div>

      {/* Score */}
      <div className="text-center leading-tight">
        <div className="flex items-baseline justify-center gap-0.5">
          <span className="font-serif text-lg sm:text-xl font-bold text-[#2b1410]">
            {rating.toFixed(1)}
          </span>
          <span className="text-[9px] font-medium text-[#7c5a4e]">/{scale}</span>
        </div>
        <div className="text-[9px] sm:text-[10px] text-[#7c5a4e] leading-tight mt-0.5">
          {count} {tr({ it: "recens.", en: "reviews", es: "reseñas", fr: "avis", de: "Bew." }, lang)}
        </div>
        {sub && (
          <div className="text-[8px] font-bold uppercase tracking-widest text-[#4a1c17] mt-0.5">
            {tr(sub, lang)}
          </div>
        )}
      </div>
    </a>
  );
}

function _avgFive(platforms) {
  const norm = platforms
    .filter((p) => p && p.enabled !== false && p.rating > 0 && p.count > 0)
    .map((p) => ({
      score: (Number(p.rating) / (p.scale || 5)) * 5,
      w: Number(p.count) || 0,
    }));
  if (!norm.length) return { avg: 0, total: 0 };
  const total = norm.reduce((s, x) => s + x.w, 0);
  const avg = norm.reduce((s, x) => s + x.score * x.w, 0) / Math.max(1, total);
  return { avg, total };
}

export function ExternalReviewsBadges({ platforms, lang }) {
  const active = (platforms || []).filter((p) => p && p.enabled !== false && p.url);
  if (!active.length) return null;

  const { avg, total } = _avgFive(active);

  return (
    <section className="mt-12" data-testid="external-reviews-badges">
      <div className="mb-4">
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2b1410] tracking-tight">
          {tr({ it: "Recensioni", en: "Reviews", es: "Reseñas", fr: "Avis", de: "Bewertungen" }, lang)}
        </h2>
        <p className="mt-1 text-sm text-[#7c5a4e] italic">
          {tr({
            it: "Le voci dei nostri ospiti — clicca su una piattaforma per leggere tutto",
            en: "What our guests say — tap a platform to read more",
            es: "Lo que dicen nuestros clientes — pulsa una plataforma para leer",
            fr: "Ce que disent nos hôtes — cliquez pour lire davantage",
            de: "Was unsere Gäste sagen — tippen Sie auf eine Plattform",
          }, lang)}
        </p>
      </div>

      {/* Aggregate hero */}
      <div className="rounded-2xl border border-[#e8dcc8] bg-gradient-to-br from-white to-[#faf3ee] p-5 shadow-sm">
        <div className="flex items-center gap-5">
          <div className="text-5xl font-serif font-bold text-[#4a1c17] leading-none">
            {avg.toFixed(1)}
          </div>
          <div>
            <div className="flex items-center gap-0.5" aria-label={`${avg.toFixed(1)} stars`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`h-4 w-4 ${
                    n <= Math.round(avg) ? "fill-amber-400 text-amber-400" : "text-[#e8dcc8]"
                  }`}
                />
              ))}
            </div>
            <div className="mt-1 text-xs text-[#7c5a4e]">
              <span className="font-semibold text-[#4a1c17]">{total}</span>{" "}
              {tr({
                it: "recensioni totali su",
                en: "total reviews across",
                es: "reseñas totales en",
                fr: "avis au total sur",
                de: "Bewertungen insgesamt auf",
              }, lang)}{" "}
              <span className="font-semibold text-[#4a1c17]">{active.length}</span>{" "}
              {tr({ it: "piattaforme", en: "platforms", es: "plataformas", fr: "plateformes", de: "Plattformen" }, lang)}
            </div>
          </div>
        </div>
      </div>

      {/* Single row of 5 platform tiles */}
      <div className="mt-4 grid grid-cols-5 gap-2 sm:gap-3">
        {active.map((p) => (
          <PlatformTile key={p.id} platform={p} lang={lang} />
        ))}
      </div>
    </section>
  );
}
